import {isUndefined} from 'lodash';
import React, {FC, ReactNode, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {InlineBanner, InlineBannerTypesEnum} from '../../../components/inlineBanner/inlineBanner';
import {RuleExpressionCardsWrapper} from './ruleExpressionCardsWrapper';

/*
 * Types.
 */

export interface RuleMultilineParameterCardProps {
  title: ReactNode;
  icon: ReactNode;

  collapsableParameters?: ReadonlyArray<ReactNode>;
  shouldExpandCollapsableParameters?: boolean;
  footer?: ReactNode;
  errorMessage?: ReactNode;
  errorType?: InlineBannerTypesEnum;
  interactionId?: string;
  onCancel?: Handler;
}

/*
 * Styles.
 */

export const StyledMultilineRuleParameterCard = styled.div`
  background: ${(p) => p.theme.greys.white};
  border-radius: 16px;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  padding: 10px 12px;
  display: grid;
  grid-template-areas: 'icon title buttons' 'content content content' 'error error error' 'optional optional optional' 'footer footer footer';
  grid-template-columns: 20px auto auto;
  grid-column-gap: 10px;
  grid-auto-rows: 1.2fr auto auto auto auto;
  align-items: center;
  width: 100%;

  ${(p) => boxShadow(p.theme, `0px 1px 9px ${p.theme.alphas.black20}`)};

  ${RuleExpressionCardsWrapper} & {
    ${(p) => boxShadow(p.theme, `0px 1px 1px ${p.theme.alphas.black10}`)};
  }
`;

export const StyledMultilineRuleParameterCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  grid-area: icon;
`;

const genericFontStyles = css`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
`;

export const StyledMultilineRuleParameterCardTitle = styled.div`
  ${genericFontStyles};
  grid-area: title;
  height: 30px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
`;

export const MultilineRuleParameterCardDefaultTitleStyled = styled.span`
  margin-right: 5px;
  white-space: nowrap;
`;

export const StyledMultilineRuleParameterCardButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

export const StyledMultilineRuleParameterCardButton = styled(ChromelessButton)`
  padding: 5px;
  border-radius: 7px;
  background: ${(p) => p.theme.alphas.transparent};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${(p) => p.theme.greys.shade50};
  margin-left: 8px;

  &:hover {
    background: ${(p) => p.theme.greys.shade20};
    color: ${(p) => p.theme.greys.shade70};
    transition: background 0.1s, color 0.1s;
  }
`;

const MultilineRuleParameterCardExpandButtonStyled = styled(StyledMultilineRuleParameterCardButton)<{
  $hideDivider?: boolean;
}>`
  position: relative;

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 5px;
    bottom: 5px;
    right: -4px;
    border-right: 1px solid ${(p) => p.theme.greys.shade40};
    display: ${({$hideDivider}) => ($hideDivider ? 'none' : 'block')};
  }
`;

export const MultilineRuleParameterCardContentStyled = styled.div`
  ${genericFontStyles};
  grid-area: content;
  margin-top: 5px;
`;

export const MultilineRuleParameterCardFooterStyled = styled.div`
  ${genericFontStyles};
  grid-area: footer;
  margin-top: 5px;
`;

export const MultilineRuleParameterCardDefaultContentStyled = styled.div`
  min-height: 30px;
`;

export const MultilineRuleParameterCardOptionalStyled = styled.div`
  ${genericFontStyles};
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  grid-area: optional;
  /* Negative margin needed to override the left and right padding of the
       card when displaying the top border which is used as a separator */
  margin: 10px -15px 0;
  padding: 10px 15px 0;
`;

const MultilineRuleParameterCardErrorMessageStyled = styled.div`
  margin-top: 10px;
  grid-area: error;
`;

const StyledErrorMessage = styled.span`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

export const RuleMultilineParameterCard: FC<RuleMultilineParameterCardProps> = (props) => {
  const {
    collapsableParameters,
    shouldExpandCollapsableParameters,
    footer,
    errorMessage,
    errorType,
    icon,
    title,
    children,
    interactionId,
    onCancel,
  } = props;
  const hasCollapsableParameters = collapsableParameters && collapsableParameters.length > 0;
  const [localShowCollapsableParameters, setLocalShowCollapsableParameters] = useState<boolean>();

  const showCollapsableParameters = useMemo(
    () => localShowCollapsableParameters ?? Boolean(shouldExpandCollapsableParameters),
    [localShowCollapsableParameters, shouldExpandCollapsableParameters],
  );

  function maybeRenderCollapsableParametersButton() {
    if (!hasCollapsableParameters) {
      return null;
    }

    return (
      <MultilineRuleParameterCardExpandButtonStyled
        $hideDivider={isUndefined(onCancel)}
        onClick={() => setLocalShowCollapsableParameters(!showCollapsableParameters)}
      >
        {showCollapsableParameters ? <Icon name="chevronUpSmall" /> : <Icon name="chevronDownSmall" />}
      </MultilineRuleParameterCardExpandButtonStyled>
    );
  }

  function maybeRenderCancelButton() {
    if (!onCancel) {
      return null;
    }

    return (
      <StyledMultilineRuleParameterCardButton onClick={onCancel}>
        <Icon name="closeSmall" />
      </StyledMultilineRuleParameterCardButton>
    );
  }

  function maybeRenderChildren() {
    if (!children) {
      return null;
    }

    return <MultilineRuleParameterCardContentStyled>{children}</MultilineRuleParameterCardContentStyled>;
  }

  function maybeRenderCollapsableParameters() {
    if (!showCollapsableParameters) {
      return null;
    }

    return (
      <MultilineRuleParameterCardOptionalStyled>
        {collapsableParameters}
      </MultilineRuleParameterCardOptionalStyled>
    );
  }

  function maybeRenderFooter() {
    if (!footer) {
      return null;
    }

    return <MultilineRuleParameterCardFooterStyled>{footer}</MultilineRuleParameterCardFooterStyled>;
  }

  function maybeRenderErrorMessage() {
    if (!errorMessage) {
      return null;
    }

    return (
      <MultilineRuleParameterCardErrorMessageStyled>
        <InlineBanner
          size={VisualSizesEnum.SMALL}
          type={errorType ?? InlineBannerTypesEnum.ERROR}
          title={<StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
          hasSmallBorderRadius
          hasColorfulText
        />
      </MultilineRuleParameterCardErrorMessageStyled>
    );
  }

  return (
    <StyledMultilineRuleParameterCard
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buildInteractionIdProps(interactionId)}
    >
      <StyledMultilineRuleParameterCardIcon>{icon}</StyledMultilineRuleParameterCardIcon>
      <StyledMultilineRuleParameterCardTitle>{title}</StyledMultilineRuleParameterCardTitle>
      <StyledMultilineRuleParameterCardButtons>
        {maybeRenderCollapsableParametersButton()}
        {maybeRenderCancelButton()}
      </StyledMultilineRuleParameterCardButtons>
      {maybeRenderChildren()}
      {maybeRenderCollapsableParameters()}
      {maybeRenderFooter()}
      {maybeRenderErrorMessage()}
    </StyledMultilineRuleParameterCard>
  );
};
